import React from 'react'
import dayjs from 'dayjs'
import {
  DATE_DISPLAY_FORMAT,
  DATE_TIME_DISPLAY_FORMAT,
  TIME_DISPLAY_12H_FORMAT,
} from 'constants/DateConstant'

const DEFAULT_EMPTY = '—'
const TIME_24H_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/
const TIME_12H_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AP]\.?M\.?)$/i
const HOUR_ONLY_RE = /^\d{1,2}$/

function parseDisplayValue(value) {
  if (value == null || value === '') return null

  if (typeof value === 'number' && value >= 0 && value <= 23) {
    const hh = String(value).padStart(2, '0')
    const timeParsed = dayjs(`2000-01-01T${hh}:00:00`)
    return timeParsed.isValid() ? timeParsed : null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null

    if (TIME_24H_RE.test(trimmed)) {
      const parts = trimmed.split(':')
      const hh = parts[0].padStart(2, '0')
      const mm = parts[1]
      const ss = parts[2] || '00'
      const timeParsed = dayjs(`2000-01-01T${hh}:${mm}:${ss}`)
      return timeParsed.isValid() ? timeParsed : null
    }

    const match12 = trimmed.match(TIME_12H_RE)
    if (match12) {
      let hours = parseInt(match12[1], 10)
      const minutes = match12[2]
      const meridiem = match12[4].toUpperCase().replace(/\./g, '')
      if (meridiem === 'PM' && hours < 12) hours += 12
      if (meridiem === 'AM' && hours === 12) hours = 0
      const hh = String(hours).padStart(2, '0')
      const timeParsed = dayjs(`2000-01-01T${hh}:${minutes}:00`)
      return timeParsed.isValid() ? timeParsed : null
    }

    if (HOUR_ONLY_RE.test(trimmed)) {
      const hour = parseInt(trimmed, 10)
      if (hour >= 0 && hour <= 23) {
        const hh = String(hour).padStart(2, '0')
        const timeParsed = dayjs(`2000-01-01T${hh}:00:00`)
        return timeParsed.isValid() ? timeParsed : null
      }
    }
  }

  const parsed = dayjs(value)
  return parsed.isValid() ? parsed : null
}

/**
 * Format a date/time value for UI display.
 * Default: Thu 24 Sep 2026 12:00 PM
 * Time-only strings like "09:00" / "17:00" → 9:00 AM / 5:00 PM
 *
 * @param {string|number|Date|import('dayjs').Dayjs|null|undefined} value
 * @param {Object} [options]
 * @param {string} [options.format] - dayjs format string
 * @param {'datetime'|'date'|'time'} [options.variant='datetime']
 * @param {string} [options.emptyPlaceholder='—']
 * @returns {string}
 */
export function formatDisplayDateTime(value, options = {}) {
  const {
    format,
    variant = 'datetime',
    emptyPlaceholder = DEFAULT_EMPTY,
  } = options

  const parsed = parseDisplayValue(value)
  if (!parsed) return emptyPlaceholder

  const resolvedFormat =
    format ||
    (variant === 'date'
      ? DATE_DISPLAY_FORMAT
      : variant === 'time'
        ? TIME_DISPLAY_12H_FORMAT
        : DATE_TIME_DISPLAY_FORMAT)

  return parsed.format(resolvedFormat)
}

/**
 * Display date/time in app format.
 * Use as <DateTimeDisplay value={createdAt} />
 * Variants: datetime (default), date, time
 */
const DateTimeDisplay = ({
  value,
  format,
  variant = 'datetime',
  emptyPlaceholder = DEFAULT_EMPTY,
  className,
  style,
  ...rest
}) => {
  const formatted = formatDisplayDateTime(value, {
    format,
    variant,
    emptyPlaceholder,
  })

  return (
    <span className={className} style={style} {...rest}>
      {formatted}
    </span>
  )
}

export default DateTimeDisplay
